import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Check, Play, TrendingUp, Users, BarChart3 } from 'lucide-react';
import Button from '../../../components/ui/Button';

const GuidedOnboarding = ({ plan, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const onboardingSteps = [
    {
      icon: Play,
      title: 'Welcome to Your Premium Experience',
      description: 'Let\'s take a quick tour of your new features and how to use them effectively.',
      tips: [
        'Access all features from the navigation menu',
        'Your data syncs automatically across all devices',
        'Premium features are marked with a star icon'
      ],
      color: 'blue'
    },
    {
      icon: TrendingUp,
      title: 'Advanced Analytics Dashboard',
      description: 'Discover patterns and trends in your symptom data with powerful analytics tools.',
      tips: [
        'View symptom correlations and trigger analysis',
        'Get predictive insights based on your history',
        'Export detailed reports for your healthcare provider'
      ],
      color: 'purple'
    },
    {
      icon: Users,
      title: 'Caregiver Management',
      description: 'Connect with family members or healthcare providers to share your health journey.',
      tips: [
        'Invite caregivers via email or unique code',
        'Control what data each caregiver can see',
        'Receive alerts when caregivers check your status'
      ],
      color: 'green',
      available: plan?.features?.caregiver_management
    },
    {
      icon: BarChart3,
      title: 'Extended History & Exports',
      description: 'Access your complete symptom history and generate unlimited doctor summaries.',
      tips: [
        `View up to ${plan?.limits?.symptom_history_days === -1 ? 'unlimited' : plan?.limits?.symptom_history_days + ' days'} of symptom history`,
        'Export data in multiple formats (PDF, CSV, Email)',
        'Schedule automatic weekly or monthly reports'
      ],
      color: 'indigo'
    }
  ];

  const availableSteps = onboardingSteps?.filter(step => step?.available !== false);
  const totalSteps = availableSteps?.length;
  const currentStepData = availableSteps?.[currentStep];

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    indigo: 'bg-indigo-100 text-indigo-600'
  };

  const IconComponent = currentStepData?.icon;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Feature Tour</span>
          <span className="text-sm text-gray-600">
            Step {currentStep + 1} of {totalSteps}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          ></div>
        </div>
      </div>
      {/* Step Content */}
      <div className="mb-8">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${colorClasses?.[currentStepData?.color]}`}>
          <IconComponent className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{currentStepData?.title}</h3>
        <p className="text-gray-600 mb-6">{currentStepData?.description}</p>

        {/* Tips */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Quick Tips:</h4>
          <ul className="space-y-2">
            {currentStepData?.tips?.map((tip, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {/* Navigation Buttons */}
      <div className="flex justify-between items-center">
        <Button 
          onClick={handlePrevious}
          variant="outline"
          disabled={currentStep === 0}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Previous
        </Button>
        <div className="flex gap-2">
          {availableSteps?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentStep ? 'bg-blue-600 w-6' : 'bg-gray-300'
              }`}
              aria-label={`Go to step ${index + 1}`}
            />
          ))}
        </div>
        <Button 
          onClick={handleNext}
          variant="default"
          iconName={currentStep === totalSteps - 1 ? 'Check' : 'ChevronRight'}
          iconPosition="right"
        >
          {currentStep === totalSteps - 1 ? 'Get Started' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default GuidedOnboarding;